import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaCheck, FaHeadset, FaPlus } from "react-icons/fa";
import SectionEyebrow from "./SectionEyebrow";
import BrandButton from "./BrandButton";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const viewport = { once: false, amount: 0.15 };

const faqs = [
  {
    question: "What services does MK AI Technologies offer?",
    answer:
      "We build AI voice agents and Twilio IVR systems, handle A2P 10DLC registration, set up GoHighLevel, and develop custom web and mobile apps for growing businesses.",
  },
  {
    question: "What can an AI voice agent do for my business?",
    answer:
      "An AI voice agent automates routine phone tasks such as scheduling appointments, qualifying leads, sending meeting reminders and transferring calls to live agents, so you save time and reduce operating costs.",
  },
  {
    question: "Should I choose a pre-built or a custom AI voice agent?",
    answer:
      "Pre-built platforms such as Vapi and Retell AI with Twilio are faster to launch (about 1-2 weeks) and cost less to build, but carry higher monthly usage fees. A custom solution built on OpenAI and Twilio takes longer (about 3-4 weeks) and costs more upfront, but offers full customization and very low ongoing costs.",
  },
  {
    question: "What is A2P 10DLC and do I need it?",
    answer:
      "A2P 10DLC is the carrier-mandated registration for businesses that send SMS to US customers from a local 10-digit number. Without an approved brand and campaign, your messages can be filtered or blocked. We handle the complete registration, from brand verification to campaign submission, across Twilio, GoHighLevel and other messaging platforms.",
  },
  {
    question: "Can you fix problems with my existing Twilio or GoHighLevel setup?",
    answer:
      "Yes. We resolve configuration issues between GoHighLevel and Twilio, fix messaging problems, and handle A2P brand and campaign registration, including resubmission after rejections, so your messages can be delivered reliably.",
  },
  {
    question: "How can we work together?",
    answer:
      "We offer two models. Flexible Hours gives you dedicated development support for ongoing development, integrations, technical support and product improvements. Custom Quote suits clearly defined projects with specific requirements and timelines, such as web apps, AI and automation, and custom solutions.",
  },
  {
    question: "Do you provide ongoing support?",
    answer:
      "Yes. Our Flexible Hours model covers ongoing technical support and product improvements, and our team is available to help with any questions or issues.",
  },
  {
    question: "How do we get started?",
    answer:
      "Send us a message through the contact form, email contact@mkaitechnology.com or call +1 737-343-8150. Tell us about your project and our team will get back to you to arrange a free consultation.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const supportPoints = [
  "A scoped plan for your project",
  "A realistic delivery timeline",
  "A clear, itemized quote",
];

const FAQItem = ({ item, index, isOpen, onToggle }) => {
  const buttonId = `faq-button-${index}`;
  const panelId = `faq-panel-${index}`;

  return (
    <div
      className={`overflow-hidden rounded-xl border transition-[border-color,box-shadow,background-color] duration-200 ${
        isOpen
          ? "border-[#8B4AFA]/35 bg-white shadow-[0_18px_40px_-28px_rgba(76,29,149,0.5)]"
          : "border-slate-200/90 bg-slate-50/80 hover:border-[#8B4AFA]/30 hover:bg-white"
      }`}
    >
      <h3>
        <button
          id={buttonId}
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center gap-4 px-4 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#8B4AFA] sm:px-5"
        >
          <span
            aria-hidden="true"
            className={`flex h-7 w-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums transition-colors duration-200 ${
              isOpen
                ? "bg-[#8B4AFA] text-white"
                : "bg-[#8a4afa1c] text-[#6e3fc7]"
            }`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className={`flex-1 font-display text-[0.95rem] font-semibold leading-snug transition-colors duration-200 sm:text-base ${
              isOpen ? "text-[#6e3fc7]" : "text-slate-900"
            }`}
          >
            {item.question}
          </span>
          <FaPlus
            aria-hidden="true"
            className={`h-3 w-3 shrink-0 transition-transform duration-200 ${
              isOpen ? "rotate-45 text-[#8B4AFA]" : "text-slate-400"
            }`}
          />
        </button>
      </h3>

      {/* Answer stays in the DOM (SEO + schema match); grid-rows animates open/close */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="px-4 pb-5 text-sm leading-7 text-slate-600 sm:pl-[4.25rem] sm:pr-10 sm:text-[0.95rem]">
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="bg-white py-24 lg:py-28">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger}
          className="mx-auto mb-12 max-w-3xl text-center"
        >
          <SectionEyebrow variants={reveal}>FAQ</SectionEyebrow>
          <motion.h2
            variants={reveal}
            className="mt-4 font-display text-3xl font-semibold leading-[1.15] text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-5xl"
          >
            Answers before you ask
          </motion.h2>
          <motion.p
            variants={reveal}
            className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600"
          >
            Everything you need to know about our AI agents, messaging
            compliance and how we work with your team.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={reveal}
          className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-white to-[#8B4AFA]/[0.06] p-5 shadow-[0_32px_72px_-44px_rgba(76,29,149,0.4)] sm:p-8 lg:p-10"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#8B4AFA]"
          />

          <div className="grid items-start gap-8 lg:grid-cols-[19rem_minmax(0,1fr)] lg:gap-12">
            {/* Left: support block */}
            <div className="lg:sticky lg:top-28">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#8B4AFA] text-white shadow-[0_12px_24px_-12px_rgba(139,74,250,0.9)]">
                <FaHeadset className="h-4 w-4" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-semibold leading-snug text-slate-900">
                Didn&apos;t find your answer?
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Share your project requirements and our team will get back to
                you with:
              </p>

              <ul className="mt-5 space-y-3">
                {supportPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-3 text-sm font-medium text-slate-700"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8a4afa1c] text-[#8B4AFA]"
                    >
                      <FaCheck className="h-2.5 w-2.5" />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <BrandButton
                  as={Link}
                  to="/contact"
                  className="mk-cta-button--full"
                >
                  Contact Our Team
                </BrandButton>
              </div>
            </div>

            {/* Right: accordion */}
            <div className="space-y-2.5 lg:border-l lg:border-slate-200/80 lg:pl-12">
              {faqs.map((item, index) => (
                <FAQItem
                  key={item.question}
                  item={item}
                  index={index}
                  isOpen={openIndex === index}
                  onToggle={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;